import React, {
  useCallback, useEffect, useMemo, useRef, useState, Suspense,
} from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useMutation } from '@apollo/client'
import { Map } from 'immutable'

import { usePostHogEvent } from '../../../../PostHog/index'
import { isBrowser } from '../../../../../config/environment'
import { historyRedirect } from '../../../../../services/navigation/index'
import { selectAuth } from '../../../../../services/auth/selectors'
import useCommunityEnabled from '../../../../../services/community/shared/useCommunityEnabled'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import {
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'
import {
  GET_PLAYLIST_BY_ID,
  FOLLOW_PLAYLIST,
  UNFOLLOW_PLAYLIST,
} from '../../../../../services/community/query'
import { enrichPlaylistItems } from '../../../../../services/community/identity/library/content-utils'
import { getPlaylistDisplayName } from '../../../../../services/community/index'
import type { GetPlaylistByIdResponse } from '../../../../../services/community/types'

import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import ErrorPage from '../../../../ErrorPage/index'
import { ERROR_TYPE_404 } from '../../../../ErrorPage/types'
import PlaylistSharedView from '../PlaylistSharedView/index'
import { withCommunityErrorBoundary } from '../../../shared/ErrorHandling/withCommunityErrorBoundary/index'

const PlaylistSharePreview = React.lazy(() => import('../PlaylistSharePreview/index'))

const RE_SHARED_PLAYLIST_PATH = /^\/playlist\/shared\/(.+)$/

function extractPlaylistIdFromPath (location: { pathname?: string }): string | null {
  const pathname = location?.pathname || ''
  const match = pathname.match(RE_SHARED_PLAYLIST_PATH)
  return match ? match[1] : null
}

interface PlaylistSharedPageProps {
  history: { push: (path: string) => void }
  location: { pathname?: string }
}

// This page renders three children — PlaylistSharedView, PlaylistSharePreview,
// and PlaylistAccessDenied — each of which wraps its own static-text slice
// via its own connectStaticText. The page itself renders no translated copy
// directly, so it has no static-text slice of its own.
function PlaylistSharedPage ({
  history,
  location,
}: PlaylistSharedPageProps) {
  const { enabled: isCommunityProfileEnabled, loading: communityLoading } = useCommunityEnabled()

  const auth = useSelector(selectAuth)
  const user = useSelector((state: { user: Map<string, unknown> }) => state.user)

  const loggedInUid = auth.get('uid')
  const isLoggedIn = !!loggedInUid
  const jwt = auth.get('jwt')
  const language = usePrimaryAppLanguage(user)

  // Member view requires both login AND the community feature flag.
  // Everyone else (logged-out, or logged-in but not in the flag) sees the anon preview.
  const showMemberView = isLoggedIn && isCommunityProfileEnabled

  const playlistId = extractPlaylistIdFromPath(location)

  // Pass JWT when logged in so the backend can resolve user-specific fields
  // (isFollowing, contentPolicy.accessCheck.allowed, userInfo on anonymous playlists for owners)
  const queryContext = isLoggedIn ? { jwt } : undefined

  const {
    data,
    loading,
  } = useQuery<GetPlaylistByIdResponse>(GET_PLAYLIST_BY_ID, {
    variables: { playlistId, language: isLoggedIn ? [language, 'en'] : ['en'] },
    skip: !playlistId,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: queryContext,
  })

  const [followPlaylistMutation] = useMutation(FOLLOW_PLAYLIST, {
    context: isLoggedIn ? { jwt } : undefined,
  })

  const [unfollowPlaylistMutation] = useMutation(UNFOLLOW_PLAYLIST, {
    context: isLoggedIn ? { jwt } : undefined,
  })

  const playlist = useMemo(() => {
    const playlists = data?.playlistList?.playlists
    // Backend automatically excludes private playlists for non-owners,
    // so an empty array means the playlist is private or doesn't exist
    return playlists && playlists.length > 0 ? playlists[0] : null
  }, [data])

  // System playlists (Watch Later / Favorites) ship with system identifiers
  // in `playlistName` — translate by playlistType for any owner-facing
  // surface on this page.
  const displayPlaylistName = getPlaylistDisplayName(playlist)

  useEffect(() => {
    if (displayPlaylistName && isBrowser()) {
      document.title = `${displayPlaylistName} | Gaia`
    }
  }, [displayPlaylistName])

  // ── Page-view tracking (PostHog session-recording trigger) ─────────────
  // Fires once per mount after the playlist query settles to a found
  // playlist.  A ref guard prevents double-fires under React strict mode.
  const trackSharedPlaylistViewed = usePostHogEvent('community_shared_playlist_viewed')
  const hasTrackedSharedPlaylistView = useRef(false)
  useEffect(() => {
    if (hasTrackedSharedPlaylistView.current) return
    if (loading) return
    if (!playlistId) return
    if (!displayPlaylistName) return
    hasTrackedSharedPlaylistView.current = true
    trackSharedPlaylistViewed(displayPlaylistName, isLoggedIn)
  }, [loading, playlistId, displayPlaylistName, isLoggedIn, trackSharedPlaylistViewed])

  // ── Shared-link virality tracking ──────────────────────────────────────
  // Inbound signals from a /playlist/shared/:id landing — pair with the
  // outbound community_playlist_shared to close the virality loop. The
  // generic community_playlist_followed also fires from the follow handler
  // (different question: outbound playlist-follow vs share-link conversion).
  const trackPlaylistFollowed = usePostHogEvent('community_playlist_followed')
  const trackSharedLinkFollowed = usePostHogEvent('community_shared_link_followed')
  const trackSharedLinkSignupClicked = usePostHogEvent('community_shared_link_signup_clicked')
  const trackSharedLinkCreatorClicked = usePostHogEvent('community_shared_link_creator_clicked')

  const playlistOwnerUid = playlist?.uid
  const isOwner = !!(
    isLoggedIn
    && playlistOwnerUid
    && String(loggedInUid) === String(playlistOwnerUid)
  )

  // Track which playlist ID the local mutation state applies to.
  // When the source playlist changes, reset to server values without a double-render.
  const [mutationState, setMutationState] = useState<{
    playlistId: string | null
    isFollowing: boolean | null
    followerCount: number | null
  }>({ playlistId: null, isFollowing: null, followerCount: null })
  const [sharePreviewVisible, setSharePreviewVisible] = useState(false)

  // Compute display values: use local mutation state when it matches the
  // current playlist, otherwise fall through to server data.
  const localIsFollowing = mutationState.playlistId === playlist?.id
    && mutationState.isFollowing !== null
    ? mutationState.isFollowing
    : !!playlist?.isFollowing
  const localFollowerCount = mutationState.playlistId === playlist?.id
    && mutationState.followerCount !== null
    ? mutationState.followerCount
    : playlist?.followerCount || 0

  const setLocalIsFollowing = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setMutationState((prev) => ({
      ...prev,
      playlistId: playlist?.id || null,
      isFollowing: typeof value === 'function' ? value(prev.isFollowing ?? false) : value,
    }))
  }, [playlist?.id])

  const setLocalFollowerCount = useCallback((value: number | ((prev: number) => number)) => {
    setMutationState((prev) => ({
      ...prev,
      playlistId: playlist?.id || null,
      followerCount: typeof value === 'function' ? value(prev.followerCount ?? 0) : value,
    }))
  }, [playlist?.id])

  const handleFollow = useCallback(async () => {
    if (!isLoggedIn || !playlistId) return
    setLocalIsFollowing(true)
    setLocalFollowerCount((prev) => prev + 1)
    try {
      const response = await followPlaylistMutation({ variables: { playlistId } })
      const nextFollowing = !!response?.data?.followPlaylist?.isFollowing
      const nextFollowerCount = response?.data?.followPlaylist?.followerCount
      setLocalIsFollowing(nextFollowing)
      if (typeof nextFollowerCount === 'number') {
        setLocalFollowerCount(nextFollowerCount)
      }
      // Two events on a shared-link follow: the generic playlist-follow signal
      // and the shared-link-specific virality conversion. They answer
      // different questions and are intentionally fired side-by-side.
      trackPlaylistFollowed(playlistId)
      if (displayPlaylistName) trackSharedLinkFollowed(displayPlaylistName)
    } catch {
      setLocalIsFollowing(false)
      setLocalFollowerCount((prev) => Math.max(0, prev - 1))
    }
  }, [
    isLoggedIn, playlistId, displayPlaylistName, followPlaylistMutation,
    trackPlaylistFollowed, trackSharedLinkFollowed,
  ])

  const handleUnfollow = useCallback(async () => {
    if (!isLoggedIn || !playlistId) return
    setLocalIsFollowing(false)
    setLocalFollowerCount((prev) => Math.max(0, prev - 1))
    try {
      const response = await unfollowPlaylistMutation({ variables: { playlistId } })
      const nextFollowing = !!response?.data?.unfollowPlaylist?.isFollowing
      const nextFollowerCount = response?.data?.unfollowPlaylist?.followerCount
      setLocalIsFollowing(nextFollowing)
      if (typeof nextFollowerCount === 'number') {
        setLocalFollowerCount(nextFollowerCount)
      }
    } catch {
      setLocalIsFollowing(true)
      setLocalFollowerCount((prev) => prev + 1)
    }
  }, [isLoggedIn, playlistId, unfollowPlaylistMutation])

  const handleShare = useCallback(() => {
    setSharePreviewVisible(true)
  }, [])

  const handleShareDismiss = useCallback(() => {
    setSharePreviewVisible(false)
  }, [])

  // Shared-link virality callbacks — wired into PlaylistSharedView via the
  // sign-up CTA's onClick and the curator anchor's onClick.
  const handleSignupClicked = useCallback(() => {
    if (!displayPlaylistName) return
    trackSharedLinkSignupClicked(displayPlaylistName)
  }, [displayPlaylistName, trackSharedLinkSignupClicked])

  const creatorDisplayName = playlist?.userProfile?.displayName || undefined
  const handleCuratorClicked = useCallback(() => {
    if (!displayPlaylistName) return
    trackSharedLinkCreatorClicked(displayPlaylistName, creatorDisplayName)
  }, [displayPlaylistName, creatorDisplayName, trackSharedLinkCreatorClicked])

  // Consumption signal: fires whenever a video launches from a Community
  // playlist context. On this surface the entry_point is 'shared_link'.
  const trackPlaylistItemPlayed = usePostHogEvent('community_playlist_item_played')

  // Enrich playlist items once — used both for rendering and for resolving
  // the played item's title at PostHog-capture time.
  // Shared playlists are user-authored — render every episode the author
  // added as its own video tile. No pivot, no dedupe. System-playlist
  // pivot+collapse only applies to the viewer's own Watch Later /
  // Favorites surfaces.
  const enrichedItems = useMemo(() => {
    return enrichPlaylistItems(playlist?.playlistItems || [], {
      dedupe: false,
      pivotEpisodesToSeries: false,
    })
  }, [playlist?.playlistItems])

  // ── Episode play: navigate to video page ────────────────────────
  // Use historyRedirect instead of history.push so the marketing-layer
  // proxy is honoured for anonymous users on /video, /series, /person paths.
  const handleEpisodePlay = useCallback((
    playedContentId: string,
    path: string,
    contentType: string,
  ) => {
    if (!path || !history) return
    const absolutePath = path.startsWith('/') ? path : `/${path}`

    // Only playable content types get the fullplayer param;
    // series/course containers are landing pages, not playable videos.
    const isPlayable = contentType === 'video' || contentType === 'episode'
    const query = isLoggedIn && isPlayable
      ? { fullplayer: 'feature' }
      : undefined

    // Fire community_playlist_item_played with the shared-link entry-point +
    // viewer-membership / creator display-name attribution. Look up the
    // tile's title from enrichedItems so PostHog gets the same human-readable
    // name shown in `tile-v2__bottom-metadata-title`.
    if (playedContentId && playlistId) {
      let ownership: 'own' | 'followed' | 'other' = 'other'
      if (isOwner) ownership = 'own'
      else if (localIsFollowing) ownership = 'followed'
      const matched = enrichedItems.find(
        (item) => item.contentId != null && String(item.contentId) === playedContentId,
      )
      const contentTitle = matched?.title || undefined
      trackPlaylistItemPlayed(
        playedContentId,
        'shared_link',
        playlistId,
        undefined,
        ownership,
        showMemberView,
        contentTitle,
        creatorDisplayName,
      )
    }

    const redirectOptions = {
      history,
      url: absolutePath,
      auth,
      user,
      isLoggedIn,
      language,
      query,
      timeout: 0,
    }
    historyRedirect(redirectOptions)
  }, [
    history, isLoggedIn, auth, user, language,
    playlistId, isOwner, localIsFollowing, showMemberView, creatorDisplayName,
    enrichedItems, trackPlaylistItemPlayed,
  ])

  // Anon "Preview" link on each row of the share view: always force
  // `?fullplayer=feature` for playable content so the viewer lands on the
  // public preview clip (the marketing proxy handles the anon case), not
  // the sales landing page. Shares the same tracking + redirect plumbing
  // as handleEpisodePlay; the only delta is the unconditional fullplayer
  // param for video / episode.
  const handleEpisodePreview = useCallback((
    playedContentId: string,
    path: string,
    contentType: string,
  ) => {
    if (!path || !history) return
    const absolutePath = path.startsWith('/') ? path : `/${path}`

    const isPlayable = contentType === 'video' || contentType === 'episode'
    const query = isPlayable ? { fullplayer: 'feature' } : undefined

    if (playedContentId && playlistId) {
      let ownership: 'own' | 'followed' | 'other' = 'other'
      if (isOwner) ownership = 'own'
      else if (localIsFollowing) ownership = 'followed'
      const matched = enrichedItems.find(
        (item) => item.contentId != null && String(item.contentId) === playedContentId,
      )
      const contentTitle = matched?.title || undefined
      trackPlaylistItemPlayed(
        playedContentId,
        'shared_link',
        playlistId,
        undefined,
        ownership,
        showMemberView,
        contentTitle,
        creatorDisplayName,
      )
    }

    const redirectOptions = {
      history,
      url: absolutePath,
      auth,
      user,
      isLoggedIn,
      language,
      query,
      timeout: 0,
    }
    historyRedirect(redirectOptions)
  }, [
    history, isLoggedIn, auth, user, language,
    playlistId, isOwner, localIsFollowing, showMemberView, creatorDisplayName,
    enrichedItems, trackPlaylistItemPlayed,
  ])

  // Logged-in users wait for the community flag so follow/unfollow buttons
  // don't briefly flash in/out while the flag resolves.
  if (isLoggedIn && communityLoading) {
    return (
      <div className="playlist-shared-page playlist-shared-page--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  // No playlist ID in URL
  if (!playlistId) {
    return (
      <ErrorPage
        history={history}
        location={location}
        code={ERROR_TYPE_404}
      />
    )
  }

  // Loading
  if (loading) {
    return (
      <div className="playlist-shared-page playlist-shared-page--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  // No data found — playlist is private, doesn't exist, or query error
  if (!playlist) {
    return (
      <ErrorPage
        history={history}
        location={location}
        code={ERROR_TYPE_404}
      />
    )
  }

  return (
    <div className="playlist-shared-page">
      <PlaylistSharedView
        playlist={playlist}
        isLoggedIn={isLoggedIn}
        isOwner={isOwner}
        isFollowing={localIsFollowing}
        followerCount={localFollowerCount}
        onFollow={showMemberView ? handleFollow : undefined}
        onUnfollow={showMemberView ? handleUnfollow : undefined}
        onShare={handleShare}
        onEpisodePlay={handleEpisodePlay}
        onEpisodePreview={handleEpisodePreview}
        onSignupClicked={handleSignupClicked}
        onCuratorClicked={handleCuratorClicked}
      />
      {sharePreviewVisible ? (
        <Suspense fallback={null}>
          <PlaylistSharePreview
            visible
            playlistId={playlistId}
            playlistName={displayPlaylistName}
            description={playlist.description || undefined}
            items={enrichedItems}
            onDismiss={handleShareDismiss}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

const PlaylistSharedPageWithBoundary = withCommunityErrorBoundary(PlaylistSharedPage)

export { PlaylistSharedPage }
export default PlaylistSharedPageWithBoundary
